import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaArrowLeft, FaTag } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getBlogById } from '../firebase/db';
import {
  normalizeLang,
  getLocalizedBlogTitle,
  getLocalizedBlogCategory,
  getLocalizedBlogContent,
  getLocalizedBlogAuthor,
  getLocalizedBlogTag,
  translateBlogText
} from '../utils/blogLocalization';
import './Blogs.css';

const BlogPost = () => {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language);
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setTranslateTick] = useState(0);

  useEffect(() => {
    getBlogById(id).then(data => {
      setBlog(data);
      if (data) {
        const title = getLocalizedBlogTitle(data, currentLang);
        document.title = `${title} | Digvijay Industries`;
      }
      setLoading(false);
    });
    window.scrollTo(0, 0);
  }, [id, currentLang]);

  // Background auto-translation fallback for any dynamic blog lacking direct _mr / _kn
  useEffect(() => {
    if (!blog || currentLang === 'en') return;
    let isMounted = true;
    const hasField = currentLang === 'mr' ? (blog.title_mr || blog.titleMr) : (blog.title_kn || blog.titleKn);

    if (!hasField) {
      (async () => {
        if (blog.title) await translateBlogText(blog.title, currentLang);
        if (blog.excerpt) await translateBlogText(blog.excerpt, currentLang);
        if (blog.content) await translateBlogText(blog.content, currentLang);
        if (blog.category) await translateBlogText(blog.category, currentLang);
        if (blog.author) await translateBlogText(blog.author, currentLang);
        if (isMounted) setTranslateTick(prev => prev + 1);
      })();
    }
    return () => { isMounted = false; };
  }, [blog, currentLang]);

  // Render markdown-style bold text and bullet points
  const renderContent = (text) => {
    if (!text) return '';
    return text.split('\n').map((line) => {
      const boldLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (line.startsWith('- ')) {
        return `<li>${line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`;
      }
      if (line.trim() === '') return '<br/>';
      return `<p>${boldLine}</p>`;
    }).join('');
  };

  if (loading) return (
    <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
      <div className="skeleton-card" style={{ height: '600px', borderRadius: '20px' }}></div>
    </div>
  );

  if (!blog) return (
    <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
      <h2>{t('blogNotFound')}</h2>
      <Link to="/blogs" className="btn btn-primary" style={{ marginTop: '20px' }}>{t('backToBlogs')}</Link>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="blog-post-page"
    >
      <div className="container">
        <div className="blog-post">
          <div className="blog-post-top-nav">
            <Link to="/blogs" className="back-btn">
              <FaArrowLeft /> {t('backToAllArticles')}
            </Link>
          </div>

          <div className="blog-detail-grid">
            {/* Left Section: Image */}
            <div className="blog-detail-left">
              <div className="blog-detail-image-card">
                <img
                  src={blog.image || 'https://via.placeholder.com/800x450'}
                  alt={getLocalizedBlogTitle(blog, currentLang)}
                  className="blog-detail-image"
                />
              </div>
            </div>

            {/* Right Section: Text Content */}
            <div className="blog-detail-right">
              <div className="blog-post-header">
                {blog.category && (
                  <span className="blog-category-badge">
                    {getLocalizedBlogCategory(blog.category, t, currentLang, blog)}
                  </span>
                )}
                <h1 className="blog-post-title">{getLocalizedBlogTitle(blog, currentLang)}</h1>
                <div className="blog-meta">
                  {(blog.date || blog.createdAt) && (
                    <span>
                      <FaCalendarAlt /> {blog.date || new Date(blog.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                  {blog.author && (
                    <span><FaUser /> {getLocalizedBlogAuthor(blog.author, currentLang, blog)}</span>
                  )}
                </div>
              </div>

              <div
                className="blog-post-content"
                dangerouslySetInnerHTML={{ __html: renderContent(getLocalizedBlogContent(blog, currentLang)) }}
              />

              {blog.tags && blog.tags.length > 0 && (
                <div className="blog-tags-wrapper">
                  <div className="blog-tags">
                    <FaTag />
                    {blog.tags.map(tag => (
                      <span key={tag} className="tag">{getLocalizedBlogTag(tag, currentLang)}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="blog-post-footer">
                <Link to="/blogs" className="btn btn-primary">← {t('backToAllArticles')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPost;
